import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MdDelete, MdInfoOutline, MdVisibility, MdDownload, MdSearch } from 'react-icons/md';

const FILE_ASSET_GUIDE_STEPS = [
  {
    color: '#3b82f6',
    title: 'Prepare Your Files',
    desc: 'Ensure your file (image, JSON, CSV, etc.) is ready. Files uploaded here are stored in the server\'s asset directory for easy access.',
  },
  {
    color: '#10b981',
    title: 'Upload to Assets',
    desc: 'Click "Upload New File" to add it to your account assets. Once uploaded, the file becomes available for your API requests in the suite.',
  },
  {
    color: '#f59e0b',
    title: 'Reference in APIs',
    desc: 'In API Collection or Test Steps, set the body mode to "Multipart Form Data", select "File" as the type, and enter the exact filename.',
  },
  {
    color: '#8b5cf6',
    title: 'Automatic Link',
    desc: 'During execution, the tool automatically retrieves the file from these assets. No need to specify full paths, just the filename.',
  },
];

function FileAssetQuickStartGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ marginBottom: '16px', padding: '14px 18px', border: '1px solid var(--border)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
      >
        <MdInfoOutline color="var(--primary)" size={18} />
        <strong style={{ fontSize: '14px' }}>File Assets Manager — Quick Start Guide</strong>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
          {FILE_ASSET_GUIDE_STEPS.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start',
              background: 'var(--bg-input)', borderLeft: `4px solid ${s.color}`,
              borderRadius: '8px', padding: '12px',
            }}>
              <div style={{
                minWidth: '24px', height: '24px', borderRadius: '50%',
                backgroundColor: s.color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '12px', flexShrink: 0,
              }}>{i + 1}</div>
              <div>
                <strong style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>{s.title}</strong>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{s.desc}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FileAssets({ authToken }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (authToken) {
      fetchFiles();
    }
  }, [authToken]);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const fetchFiles = async () => {
    try {
      const response = await axios.get('/api/files', {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      setFiles(response.data);
    } catch (err) {
      setError('Failed to fetch files: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
    }
  };

  const handleViewFile = async (file) => {
    setPreviewFile(file);
    setShowPreview(true);
    setPreviewContent(null);
    setLoadingPreview(true);
    setError('');

    const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(file.name);
    const isText = /\.(json|csv|txt|log|xml|html|css|js|py|md|yaml|yml)$/i.test(file.name);

    try {
      const config = {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      };

      if (isImage) {
        // Fetch image as blob to include Auth header
        const response = await axios.get(`/api/files/${encodeURIComponent(file.name)}`, { 
          ...config,
          responseType: 'blob' 
        });
        const url = URL.createObjectURL(response.data);
        setPreviewContent(url);
      } else if (isText) {
        const response = await axios.get(`/api/files/${encodeURIComponent(file.name)}`, { 
          ...config,
          responseType: 'text' 
        });
        setPreviewContent(response.data);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
      setError('Failed to load preview: ' + msg);
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreview = () => {
    if (previewContent && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(previewFile?.name)) {
      URL.revokeObjectURL(previewContent);
    }
    setShowPreview(false);
    setPreviewFile(null);
    setPreviewContent(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post('/api/files/upload', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        }
      });
      setSuccess(`File "${file.name}" uploaded successfully.`);
      fetchFiles();
    } catch (err) {
      setError('Upload failed: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const confirmDelete = (filename) => {
    setFileToDelete(filename);
    setShowConfirm(true);
  };

  const handleDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      await axios.delete(`/api/files/${encodeURIComponent(fileToDelete)}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      setSuccess(`File "${fileToDelete}" deleted.`);
      fetchFiles();
    } catch (err) {
      setError('Delete failed: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
    } finally {
      setShowConfirm(false);
      setFileToDelete(null);
    }
  };

  const filteredFiles = files.filter((file) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;

    return [
      file.name || '',
      String(file.size || ''),
      file.createdAt ? new Date(file.createdAt).toLocaleString() : ''
    ].join(' ').toLowerCase().includes(query);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h1 style={{ margin: 0 }}>File Assets Manager</h1>
        {showSearch ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'min(460px, 100%)' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by filename, size, or date"
              style={{ minWidth: '220px' }}
            />
            <button
              type="button"
              className="icon-btn"
              onClick={() => setShowSearch(false)}
              aria-label="Close search"
              title="Close search"
            >
              x
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="icon-btn"
            onClick={() => setShowSearch(true)}
            aria-label="Show search"
            title="Search"
          >
            <MdSearch />
          </button>
        )}
      </div>

      <FileAssetQuickStartGuide />

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="action-buttons" style={{ marginBottom: '20px' }}>
        <button 
          className="btn btn-primary" 
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload New File'}
        </button>
        <input 
          type="file" 
          ref={fileInputRef} 
          style={{ display: 'none' }} 
          onChange={handleFileUpload} 
        />
      </div>

      <div className="card">
        <h2>Available Files ({filteredFiles.length})</h2>
        {files.length === 0 ? (
          <p>No files uploaded yet.</p>
        ) : filteredFiles.length === 0 ? (
          <p>No file assets found for "<strong>{searchTerm}</strong>". Try a different search.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Filename</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file) => (
                  <tr key={file.name}>
                    <td><code>{file.name}</code></td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                        <button 
                          id={`view-${file.name.replace(/\.[^/.]+$/, "")}`}
                          className="btn btn-primary btn-icon-sq" 
                          onClick={() => handleViewFile(file)}
                          title="View File"
                        >
                          <MdVisibility />
                        </button>
                        <button 
                          id={`delete-${file.name.replace(/\.[^/.]+$/, "")}`}
                          className="btn btn-danger btn-icon-sq" 
                          onClick={() => confirmDelete(file.name)}
                          title="Delete File"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showPreview && previewFile && (
        <div className="modal-overlay" onClick={closePreview}>
          <div className="modal-content" style={{ width: 'min(90%, 900px)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="preview-modal-title"><MdVisibility style={{ marginRight: '8px', verticalAlign: 'middle' }} /> {previewFile.name}</h2>
              <button id="close-preview-modal" className="modal-close" onClick={closePreview}>x</button>
            </div>
            <div id="preview-modal-content" className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
              {error && <div className="error" style={{ marginBottom: '16px' }}>{error}</div>}
              
              {loadingPreview ? (
                <div className="loading">Loading content...</div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(previewFile.name) && previewContent ? (
                    <img 
                      id="preview-image"
                      src={previewContent} 
                      alt={previewFile.name} 
                      style={{ maxWidth: '100%', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }} 
                    />
                  ) : previewContent !== null ? (
                    <pre 
                      id="preview-text"
                      style={{ 
                        width: '100%',
                        padding: '16px', 
                        background: 'var(--bg-input)', 
                        borderRadius: '8px', 
                        fontSize: '13px', 
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        color: 'var(--text-main)',
                        border: '1px solid var(--border)'
                      }}>
                      {typeof previewContent === 'object' ? JSON.stringify(previewContent, null, 2) : String(previewContent)}
                    </pre>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <p>{error ? 'Preview could not be loaded.' : 'Preview not available for this file type.'}</p>
                      <a 
                        href={`/api/files/${encodeURIComponent(previewFile.name)}`} 
                        download={previewFile.name}
                        className="btn btn-primary"
                        style={{ marginTop: '10px' }}
                        onClick={(e) => {
                          // Ensure we download with Auth header if needed or just use standard anchor
                          // Simple anchor might fail if backend requires auth for the download too
                        }}
                      >
                        <MdDownload style={{ marginRight: '8px' }} /> Download File
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closePreview}>Close</button>
              <a 
                href={`/api/files/${encodeURIComponent(previewFile.name)}`} 
                download={previewFile.name}
                className="btn btn-primary"
              >
                <MdDownload style={{ marginRight: '8px' }} /> Download
              </a>
            </div>
          </div>
        </div>
      )}

      {showConfirm && (
        <div className="modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="modal-content modal-content-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Deletion</h2>
              <button className="modal-close" onClick={() => setShowConfirm(false)}>x</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{fileToDelete}</strong>?</p>
              <p style={{ marginTop: '10px', fontSize: '13px', color: 'var(--text-muted)' }}>
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteFile}>Delete File</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FileAssets;
